import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import type { Conversation, Draft } from './api';

type S = components['schemas'];
export type ReviewManifest = S['ReviewManifest'];
export type DraftWarning = S['DraftWarning'];
export type DraftRecipientInput = S['DraftRecipientInput'];

/** Draft states a person can still change before anything is handed to the provider. */
export const EDITABLE: ReadonlySet<Draft['state']> = new Set(['draft', 'pending_review', 'revoked', 'failed']);
/** Draft states shown as the live draft of a conversation. */
export const LIVE: ReadonlySet<Draft['state']> = new Set(['draft', 'pending_review', 'revoked', 'failed', 'approved', 'scheduled', 'sending']);

/** Prefixes refreshed after any draft command. */
export function draftInvalidations(orgId: string, conversationId?: string) {
  return [
    orgKey(orgId, 'drafts'),
    orgKey(orgId, 'outbox'),
    orgKey(orgId, 'deliveries'),
    orgKey(orgId, 'conversations', 'list'),
    ...(conversationId ? [orgKey(orgId, 'conversations', 'detail', conversationId)] : []),
  ];
}

export function draftQuery(orgId: string, draftId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'drafts', 'one', draftId),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/drafts/{draftId}', { params: { path: { orgId, draftId } }, signal })),
  });
}

export function conversationDraftsQuery(orgId: string, conversationId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'drafts', 'list', { conversation_id: conversationId }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/drafts', {
          params: { path: { orgId }, query: { conversation_id: conversationId, limit: 20 } },
          signal,
        }),
      ),
  });
}

/** Approved replies inside their send hold, soonest first. */
export function scheduledDraftsQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'drafts', 'list', { state: 'scheduled' }),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/drafts', { params: { path: { orgId }, query: { state: 'scheduled', limit: 20 } }, signal })),
    select: (page: S['DraftPage']) =>
      [...page.items].sort((a, b) => (a.send_not_before ?? '').localeCompare(b.send_not_before ?? '')),
  });
}

export function orgSettingsQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'settings', 'organization'),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/settings', { params: { path: { orgId } }, signal })),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function providersQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'llm-providers', 'usable'),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/settings/providers', { params: { path: { orgId }, query: { limit: 50 } }, signal })),
    select: (page: S['LlmProviderPage']) => page.items.filter((p) => p.enabled && p.credential_configured && !p.deleted_at),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function membersQuery(orgId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'memberships', 'names'),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/memberships', { params: { path: { orgId }, query: { status: 'active', limit: 100 } }, signal }),
      ),
    select: (page: S['MembershipPage']) => new Map(page.items.filter((m) => m.actor_id).map((m) => [m.actor_id!, m.display_name])),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * Sender identities of every connector. There is no identity list operation: each connector detail
 * carries its identities.
 */
export function useSenderIdentities() {
  const orgId = useOrgId();
  const connectors = useQuery({
    queryKey: orgKey(orgId, 'mailbox-connectors', 'all'),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/mailbox-connectors', { params: { path: { orgId }, query: { limit: 200 } }, signal })),
    staleTime: 5 * 60_000,
  });
  const usable = (connectors.data?.items ?? []).filter((c) => c.state !== 'revoked');
  const details = useQueries({
    queries: usable.map((c) => ({
      queryKey: orgKey(orgId, 'mailbox-connectors', 'detail', c.id),
      staleTime: 5 * 60_000,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          api.GET('/api/v1/orgs/{orgId}/mailbox-connectors/{connectorId}', { params: { path: { orgId, connectorId: c.id } }, signal }),
        ),
    })),
  });
  const identities = details
    .flatMap((d) => (d.data ? d.data.sender_identities.filter((si) => si.enabled && !si.deleted_at) : []))
    .sort((a, b) => Number(Boolean(b.verified_at)) - Number(Boolean(a.verified_at)));
  return {
    identities,
    pending: connectors.isPending || details.some((d) => d.isPending),
  };
}

// ---------- Commands (plain functions; components wrap them in mutations) ----------

async function submitForReview(orgId: string, draftId: string, version: number, revisionId: string) {
  return unwrap(
    api.POST('/api/v1/orgs/{orgId}/drafts/{draftId}/submit-review', {
      params: { path: { orgId, draftId }, header: { 'If-Match': ifMatch(version), 'Idempotency-Key': newIdempotencyKey() } },
      body: { revision_id: revisionId },
    }),
  );
}

/** Moves a draft into review if it is not there yet. Returns the version after the step. */
export async function ensureInReview(orgId: string, d: Draft): Promise<number> {
  if (d.state !== 'draft' && d.state !== 'revoked' && d.state !== 'failed') return d.version;
  const r = await submitForReview(orgId, d.id, d.version, d.current_revision_id);
  return r.version;
}

/**
 * Saves an edit as a new revision and puts it straight back into review, so the draft stays
 * in the queue of things to approve.
 */
export async function saveRevision(orgId: string, d: Draft, patch: S['DraftUpdate']): Promise<Draft> {
  const next = await unwrap(
    api.PATCH('/api/v1/orgs/{orgId}/drafts/{draftId}', {
      params: { path: { orgId, draftId: d.id }, header: { 'If-Match': ifMatch(d.version), 'Idempotency-Key': newIdempotencyKey() } },
      body: patch,
    }),
  );
  const r = await submitForReview(orgId, next.id, next.version, next.current_revision_id);
  return { ...next, version: r.version, state: r.state };
}

/** Freezes the exact current revision for approval. */
export async function prepareApproval(orgId: string, d: Draft): Promise<ReviewManifest> {
  const version = await ensureInReview(orgId, d);
  return unwrap(
    api.POST('/api/v1/orgs/{orgId}/outbox/review-manifests', {
      params: { path: { orgId }, header: { 'Idempotency-Key': newIdempotencyKey() } },
      body: { items: [{ draft_id: d.id, version, revision_id: d.current_revision_id }], baseline_revision_id: null },
    }),
  );
}

export async function approveManifest(orgId: string, m: ReviewManifest) {
  const it = m.items[0]!;
  return unwrap(
    api.POST('/api/v1/orgs/{orgId}/drafts/{draftId}/approve', {
      params: {
        path: { orgId, draftId: it.draft_id },
        header: { 'If-Match': ifMatch(it.draft_version), 'Idempotency-Key': newIdempotencyKey() },
      },
      body: {
        revision_id: it.draft_revision_id,
        payload_sha256: it.payload_sha256,
        expected_inbound_sequence: it.base_inbound_sequence,
        review_manifest_id: m.id,
        reason: 'Approved in the conversation',
      },
    }),
  );
}

/**
 * Stops a scheduled send. The server says whether it was in time; when it was, the draft goes
 * straight back into review so it stays in the queue of things to approve.
 */
export async function cancelSend(orgId: string, d: Draft, reason = 'Cancelled in the conversation') {
  const r = await unwrap(
    api.POST('/api/v1/orgs/{orgId}/drafts/{draftId}/revoke', {
      params: { path: { orgId, draftId: d.id }, header: { 'If-Match': ifMatch(d.version), 'Idempotency-Key': newIdempotencyKey() } },
      body: { approval_id: d.active_approval_id ?? '', reason },
    }),
  );
  if (r.outcome === 'prevented' && r.state === 'revoked') await submitForReview(orgId, d.id, r.version, d.current_revision_id);
  return r;
}

export async function discardDraft(orgId: string, d: Pick<Draft, 'id' | 'version'>) {
  return unwrap(
    api.DELETE('/api/v1/orgs/{orgId}/drafts/{draftId}', {
      params: { path: { orgId, draftId: d.id }, header: { 'If-Match': ifMatch(d.version), 'Idempotency-Key': newIdempotencyKey() } },
    }),
  );
}

/** Writes a draft by hand and puts it into review. */
export async function createReviewedDraft(
  orgId: string,
  conversationId: string,
  body: S['DraftCreate'],
  sourceVersion?: number,
): Promise<Draft & { reviewUnavailable?: boolean }> {
  const d = await unwrap(
    api.POST('/api/v1/orgs/{orgId}/conversations/{conversationId}/drafts', {
      params: { path: { orgId, conversationId }, header: { 'Idempotency-Key': newIdempotencyKey(), ...(sourceVersion !== undefined ? { 'If-Match': ifMatch(sourceVersion) } : {}) } },
      body,
    }),
  );
  try {
    const r = await submitForReview(orgId, d.id, d.version, d.current_revision_id);
    return { ...d, version: r.version, state: r.state };
  } catch {
    // Creation already committed. Preserve the receipt; review can retry this exact draft.
    return { ...d, reviewUnavailable: true };
  }
}

/**
 * One model call that writes an unapproved draft from the location and this conversation.
 * The instruction travels with the frozen context. The new draft goes straight into review.
 */
export async function generateAiDraft(
  orgId: string,
  c: Conversation,
  providerId: string,
  instruction: string | null,
): Promise<string> {
  const locationId = c.primary_location_id;
  if (!locationId) throw new Error('No site linked');
  const location = await unwrap(
    api.GET('/api/v1/orgs/{orgId}/locations/{locationId}', { params: { path: { orgId, locationId } } }),
  );
  const manifest = await unwrap(
    api.POST('/api/v1/orgs/{orgId}/context/previews', {
      params: { path: { orgId }, header: { 'Idempotency-Key': newIdempotencyKey() } },
      body: {
        location_id: locationId,
        location_version: location.version,
        conversation_id: c.id,
        selection: { current_metadata: true, existing_conversations: { mode: 'selected', ids: [c.id] } },
        operator_instruction: instruction?.trim() || null,
        on_inaccessible_selection: 'omit_with_warning',
      },
    }),
  );
  const result = await unwrap(
    api.POST('/api/v1/orgs/{orgId}/conversations/{conversationId}/llm-drafts', {
      params: { path: { orgId, conversationId: c.id }, header: { 'Idempotency-Key': newIdempotencyKey() } },
      body: {
        manifest_id: manifest.id,
        manifest_sha256: manifest.manifest_sha256,
        provider_id: providerId,
        expected_conversation_version: c.version,
        expected_inbound_sequence: c.latest_inbound_sequence,
        reason: instruction?.trim() ? `Draft with AI: ${instruction.trim()}`.slice(0, 2000) : 'Draft with AI',
      },
    }),
  );
  if (result.draft_state === 'draft') {
    const d = await unwrap(
      api.GET('/api/v1/orgs/{orgId}/drafts/{draftId}', { params: { path: { orgId, draftId: result.draft_id } } }),
    );
    await ensureInReview(orgId, d);
  }
  return result.draft_id;
}

/** "5 min hold", "1 h hold": the org's send hold after approval, cancellable until it ends. */
export function holdLabel(seconds: number | undefined): string {
  if (seconds === undefined) return 'Short hold';
  if (seconds <= 0) return 'No hold';
  const minutes = Math.round(seconds / 60);
  const text =
    minutes < 60 ? `${minutes} min` : minutes % 60 === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  return `${text} hold`;
}
